// @ts-nocheck
import { useState } from 'react';
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Code,
  Divider,
  Alert,
  ThemeIcon,
  SimpleGrid,
} from '@/components/ui';
import { IconInfoCircle, IconDatabase } from '@tabler/icons-react';
import {
  CCMSDataTable,
  CCMSPagination,
  type TableColumn,
} from '@/components/ccms-components/data-display';

/**
 * CCMS Data Display showcase page.
 *
 * These components are part of the CCMS SDK component library. In real usage
 * they are fed by the CCMS SDK HTTP client (typically via the `useSDKTable`
 * hook, which calls a backend list endpoint). For this showcase we drive them
 * with inline mock data and no-op handlers so the page is fully self-contained
 * and never performs a network request.
 */

// ---------------------------------------------------------------------------
// Inline mock data (no network) for the live previews
// ---------------------------------------------------------------------------
interface DemoMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'invited' | 'inactive';
}

const MOCK_MEMBERS: DemoMember[] = [
  { id: '1', name: 'Grace Mwangi', email: 'grace@example.org', role: 'Pastor', status: 'active' },
  { id: '2', name: 'David Okello', email: 'david@example.org', role: 'Elder', status: 'active' },
  { id: '3', name: 'Sarah Bekele', email: 'sarah@example.org', role: 'Volunteer', status: 'invited' },
  { id: '4', name: 'John Adeyemi', email: 'john@example.org', role: 'Member', status: 'inactive' },
];

const MOCK_COLUMNS: TableColumn<DemoMember>[] = [
  { key: 'name', header: 'Name', accessor: 'name', sortable: true },
  { key: 'email', header: 'Email', accessor: 'email' },
  { key: 'role', header: 'Role', accessor: 'role', sortable: true },
  {
    key: 'status',
    header: 'Status',
    accessor: 'status',
    align: 'center',
    render: (value: DemoMember['status']) => (
      <Badge
        size="sm"
        color={value === 'active' ? 'green' : value === 'invited' ? 'yellow' : 'gray'}
      >
        {String(value).toUpperCase()}
      </Badge>
    ),
  },
];

// Small reusable helper for the "key props" lines inside each doc card.
function PropLine({
  name,
  type,
  required,
  note,
}: {
  name: string;
  type: string;
  required?: boolean;
  note?: string;
}) {
  return (
    <Text size="sm">
      <Code>{name}</Code>{' '}
      <Text span c="dimmed">
        — {type} —{' '}
      </Text>
      {required ? (
        <Text span c="red" fw={600}>
          required
        </Text>
      ) : (
        <Text span c="dimmed">
          optional
        </Text>
      )}
      {note ? (
        <Text span c="dimmed">
          {' '}
          · {note}
        </Text>
      ) : null}
    </Text>
  );
}

// Small reusable "needs to run live" badge used on every doc card.
function NeedsBadge() {
  return (
    <Badge
      variant="light"
      color="grape"
      leftSection={<IconDatabase size={12} />}
      style={{ textTransform: 'none' }}
    >
      Needs: CCMS SDK HTTP client / a backend endpoint
    </Badge>
  );
}

export default function CcmsDataDisplayPage() {
  // Local-only state to make the live previews interactive without any network.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalItems = 137; // pretend server total

  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap={4}>
        <Title order={2}>CCMS Data Display (CCMSDataTable, CCMSPagination)</Title>
        <Text c="dimmed">
          SDK-bound data display components. They are designed to render the result of a CCMS SDK
          HTTP query (a paginated list endpoint), usually wired up through the{' '}
          <Code>useSDKTable</Code> hook. The live previews below are driven by inline mock data and
          no-op handlers, so this page makes no network calls.
        </Text>
      </Stack>

      <Alert
        variant="light"
        color="blue"
        title="How these connect to the backend"
        icon={<IconInfoCircle size={18} />}
      >
        In production you pass server data and handlers into these components. The{' '}
        <Code>useSDKTable</Code> hook owns the data fetching (page, page size and sort) by calling
        your CCMS SDK service method, then hands <Code>data</Code>, <Code>loading</Code>,{' '}
        <Code>totalItems</Code> and the page setters to <Code>CCMSDataTable</Code> /{' '}
        <Code>CCMSPagination</Code>. Below we skip the hook and supply mock values directly.
      </Alert>

      {/* ------------------------------------------------------------------ */}
      {/* CCMSDataTable */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>CCMSDataTable</Title>
              <Text size="sm" c="dimmed">
                A column-driven table that renders SDK list results and handles loading, error and
                empty states for you.
              </Text>
            </Stack>
            <NeedsBadge />
          </Group>

          <Divider label="Key props" labelPosition="left" />
          <Stack gap={4}>
            <PropLine name="data" type="T[]" required note="row objects (from the SDK)" />
            <PropLine name="columns" type="TableColumn<T>[]" required note="header + accessor + optional render" />
            <PropLine name="loading" type="boolean" note="shows CCMSLoadingState" />
            <PropLine name="error" type="Error | null" note="shows CCMSErrorState" />
            <PropLine name="emptyMessage" type="string" note="shown when data is empty" />
            <PropLine name="sortable" type="boolean" note="enables header sort toggles" />
            <PropLine name="onSort" type="(config: SortConfig) => void" note="fires on header click" />
            <PropLine name="onRowClick" type="(row: T) => void" note="row click handler" />
            <PropLine name="striped / highlightOnHover" type="boolean" note="visual styling" />
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`const columns: TableColumn<Member>[] = [
  { key: 'name', header: 'Name', accessor: 'name', sortable: true },
  { key: 'email', header: 'Email', accessor: 'email' },
  {
    key: 'status',
    header: 'Status',
    accessor: 'status',
    render: (v) => <Badge>{v}</Badge>,
  },
];

// Data normally comes from the SDK via useSDKTable:
const { data, loading, error, setSortConfig } = useSDKTable({
  queryFn: ({ page, pageSize, sort }) =>
    memberService.listView({ page, pageSize, sort }),
});

<CCMSDataTable
  data={data}
  columns={columns}
  loading={loading}
  error={error}
  onSort={setSortConfig}
/>;`}</Code>

          <Divider label="Live preview (mock data)" labelPosition="left" />
          <Text size="xs" c="dimmed">
            Safe to render live: CCMSDataTable is presentational (no SDK/network hook on mount). It
            is fed the inline <Code>MOCK_MEMBERS</Code> array below.
          </Text>
          <CCMSDataTable
            data={MOCK_MEMBERS}
            columns={MOCK_COLUMNS}
            sortable
            onSort={() => {}}
            onRowClick={() => {}}
          />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Card withBorder padding="sm">
              <Text size="xs" fw={600} mb={6}>
                loading state
              </Text>
              <CCMSDataTable data={[]} columns={MOCK_COLUMNS} loading />
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" fw={600} mb={6}>
                empty state
              </Text>
              <CCMSDataTable data={[]} columns={MOCK_COLUMNS} emptyMessage="No members yet" />
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" fw={600} mb={6}>
                error state
              </Text>
              <CCMSDataTable
                data={[]}
                columns={MOCK_COLUMNS}
                error={new Error('Request failed (mock)')}
              />
            </Card>
          </SimpleGrid>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* CCMSPagination */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>CCMSPagination</Title>
              <Text size="sm" c="dimmed">
                Page controls + page-size selector for an SDK-backed list. Reports the current
                window (e.g. "1-10 of 137") based on the server total.
              </Text>
            </Stack>
            <NeedsBadge />
          </Group>

          <Divider label="Key props" labelPosition="left" />
          <Stack gap={4}>
            <PropLine name="page" type="number" required note="1-based current page" />
            <PropLine name="pageSize" type="number" required note="rows per page" />
            <PropLine name="totalItems" type="number" required note="total from the server" />
            <PropLine name="onPageChange" type="(page: number) => void" required />
            <PropLine name="onPageSizeChange" type="(pageSize: number) => void" note="enables the size selector" />
            <PropLine name="pageSizeOptions" type="number[]" note="defaults to [10, 20, 50, 100]" />
            <PropLine name="showPageSize" type="boolean" note="toggle the size selector" />
            <PropLine name="gradientVariant" type="GradientVariant" note="button accent" />
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`const { page, pageSize, totalItems, setPage, setPageSize } =
  useSDKTable({ queryFn: ({ page, pageSize }) => svc.listView({ page, pageSize }) });

<CCMSPagination
  page={page}
  pageSize={pageSize}
  totalItems={totalItems}
  onPageChange={setPage}
  onPageSizeChange={setPageSize}
/>;`}</Code>

          <Divider label="Live preview (mock totals)" labelPosition="left" />
          <Text size="xs" c="dimmed">
            Safe to render live: CCMSPagination only reads local accessibility prefs from a Zustand
            store (no network on mount). It is driven by local state with a pretend{' '}
            <Code>totalItems = {String(totalItems)}</Code>.
          </Text>
          <CCMSPagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* useSDKTable (hook — doc only) */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>useSDKTable (hook)</Title>
              <Text size="sm" c="dimmed">
                The data layer for the two components above. Manages page / page size / sort state
                and fetches each page from your CCMS SDK service.
              </Text>
            </Stack>
            <Badge variant="light" color="gray" style={{ textTransform: 'none' }}>
              Doc only — hook, not a component
            </Badge>
          </Group>

          <Alert
            variant="light"
            color="grape"
            title="Why this is doc-only"
            icon={<IconDatabase size={18} />}
          >
            <Text size="sm">
              <Code>useSDKTable</Code> calls <Code>useCCMSQuery</Code>, which invokes your{' '}
              <Code>queryFn</Code> against the CCMS SDK HTTP client (a backend list endpoint) on
              mount. Rendering it live here would trigger a real network request, so it is shown as a
              snippet only.
            </Text>
          </Alert>

          <Divider label="Key options / returns" labelPosition="left" />
          <Stack gap={4}>
            <PropLine
              name="queryFn"
              type="({ page, pageSize, sort }) => Promise<{ items, totalItems }>"
              required
              note="your SDK call"
            />
            <PropLine name="initialPage / initialPageSize" type="number" note="defaults 1 / 20" />
            <PropLine name="initialSort" type="SortConfig" note="initial sort field + direction" />
            <PropLine name="enabled" type="boolean" note="gate the fetch" />
            <Text size="sm" c="dimmed" mt={4}>
              Returns: <Code>data</Code>, <Code>totalItems</Code>, <Code>page</Code>,{' '}
              <Code>pageSize</Code>, <Code>sortConfig</Code>, <Code>loading</Code>,{' '}
              <Code>error</Code>, <Code>setPage</Code>, <Code>setPageSize</Code>,{' '}
              <Code>setSortConfig</Code>, <Code>refetch</Code>.
            </Text>
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`const {
  data, loading, error,
  page, pageSize, totalItems,
  setPage, setPageSize, setSortConfig,
} = useSDKTable({
  queryFn: ({ page, pageSize, sort }) =>
    userService.listView({ page, pageSize, sort }),
  initialPageSize: 20,
});

<CCMSDataTable data={data} columns={columns} loading={loading} error={error} onSort={setSortConfig} />
<CCMSPagination
  page={page}
  pageSize={pageSize}
  totalItems={totalItems}
  onPageChange={setPage}
  onPageSizeChange={setPageSize}
/>;`}</Code>
        </Stack>
      </Card>

      <Group gap="xs" c="dimmed">
        <ThemeIcon variant="light" color="gray" size="sm" radius="xl">
          <IconInfoCircle size={14} />
        </ThemeIcon>
        <Text size="xs" c="dimmed">
          All live previews on this page use inline mock data and no-op handlers. To wire them to
          real data, supply the CCMS SDK HTTP client through a list endpoint (typically via{' '}
          <Code>useSDKTable</Code>).
        </Text>
      </Group>
    </Stack>
  );
}
